/**
 * Serialiserer og koalescerer sjekkliste-lagringer per entitet (issue #13248).
 *
 * Sjekklisten lagres som fullt HTML-innhold per PUT. Uten serialisering kan raske
 * toggles gi overlappende PUT-er og stale Ably-ekko som klobrer lokal tilstand.
 * Koordinatoren garanterer:
 *  - maks én PUT i flight per nøkkel, siste innhold vinner (mellomliggende droppes)
 *  - retry med backoff ved feil
 *  - `getLocalContent` holder lokalt innhold tilgjengelig for pending-overlayet i
 *    storen så lenge skriving pågår, pluss et grace-vindu etter siste vellykkede
 *    PUT (en liste-refetch startet før PUT-en landet kan dispatches etterpå)
 */

type ChecklistSaveFn = (content: string) => Promise<void>;

const DEBOUNCE_MS = 300;
const GRACE_MS = 10_000;
const MAX_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [1_000, 3_000];

interface PendingEntry {
  localContent: string;
  lastSavedContent?: string;
  inFlight: boolean;
  debounceTimer?: ReturnType<typeof setTimeout>;
  graceTimer?: ReturnType<typeof setTimeout>;
  retryTimer?: ReturnType<typeof setTimeout>;
  saveFn: ChecklistSaveFn;
  onFinalFailure?: (key: string, error: unknown) => void;
}

class ChecklistSaveCoordinator {
  private entries = new Map<string, PendingEntry>();

  /**
   * Registrerer nytt lokalt innhold og planlegger lagring. Innholdet er
   * tilgjengelig via `getLocalContent` synkront fra dette kallet.
   */
  save(
    key: string,
    content: string,
    saveFn: ChecklistSaveFn,
    onFinalFailure?: (key: string, error: unknown) => void,
  ): void {
    let entry = this.entries.get(key);
    if (entry === undefined) {
      entry = { localContent: content, inFlight: false, saveFn, onFinalFailure };
      this.entries.set(key, entry);
    } else {
      entry.localContent = content;
      entry.saveFn = saveFn;
      entry.onFinalFailure = onFinalFailure;
      if (entry.graceTimer !== undefined) {
        clearTimeout(entry.graceTimer);
        entry.graceTimer = undefined;
      }
      if (entry.retryTimer !== undefined) {
        // Nytt innhold erstatter retry-payloaden; flushen under tar det siste.
        clearTimeout(entry.retryTimer);
        entry.retryTimer = undefined;
      }
    }
    if (entry.inFlight) {
      // localContent er oppdatert; flushes som trailing PUT når den pågående lander.
      return;
    }
    if (entry.debounceTimer !== undefined) {
      clearTimeout(entry.debounceTimer);
    }
    entry.debounceTimer = setTimeout(() => {
      entry.debounceTimer = undefined;
      void this.flush(key, 0);
    }, DEBOUNCE_MS);
  }

  /** Lokalt innhold mens lagring pågår eller innenfor grace-vinduet, ellers undefined. */
  getLocalContent(key: string): string | undefined {
    return this.entries.get(key)?.localContent;
  }

  hasPendingWrites(key: string): boolean {
    const entry = this.entries.get(key);
    return entry !== undefined && (entry.inFlight || entry.debounceTimer !== undefined);
  }

  private async flush(key: string, attempt: number): Promise<void> {
    const entry = this.entries.get(key);
    if (entry === undefined || entry.inFlight) {
      return;
    }
    const content = entry.localContent;
    if (content === entry.lastSavedContent) {
      this.startGrace(key, entry);
      return;
    }
    entry.inFlight = true;
    try {
      await entry.saveFn(content);
      entry.inFlight = false;
      entry.lastSavedContent = content;
      if (entry.localContent !== content) {
        // Nye toggles kom mens PUT-en var i flight — send siste innhold.
        void this.flush(key, 0);
      } else {
        this.startGrace(key, entry);
      }
    } catch (error) {
      entry.inFlight = false;
      if (entry.localContent !== content) {
        // Innholdet er allerede foreldet; hopp over retry av den gamle payloaden.
        void this.flush(key, 0);
        return;
      }
      if (attempt + 1 < MAX_ATTEMPTS) {
        const backoff = RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];
        entry.retryTimer = setTimeout(() => {
          entry.retryTimer = undefined;
          void this.flush(key, attempt + 1);
        }, backoff);
      } else {
        const onFinalFailure = entry.onFinalFailure;
        this.clear(key, entry);
        onFinalFailure?.(key, error);
      }
    }
  }

  private startGrace(key: string, entry: PendingEntry): void {
    if (entry.graceTimer !== undefined) {
      clearTimeout(entry.graceTimer);
    }
    entry.graceTimer = setTimeout(() => {
      this.entries.delete(key);
    }, GRACE_MS);
  }

  private clear(key: string, entry: PendingEntry): void {
    if (entry.debounceTimer !== undefined) clearTimeout(entry.debounceTimer);
    if (entry.graceTimer !== undefined) clearTimeout(entry.graceTimer);
    if (entry.retryTimer !== undefined) clearTimeout(entry.retryTimer);
    this.entries.delete(key);
  }

  _resetForTests(): void {
    for (const [key, entry] of this.entries) {
      this.clear(key, entry);
    }
  }
}

export const checklistSaveCoordinator = new ChecklistSaveCoordinator();

export function checklistKeyForEventOccurrence(uuid: string): string {
  return 'eventOccurrences:' + uuid;
}
